import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ResetPassword = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Supabase handles the recovery hash → exchanges for session automatically
    // when detectSessionInUrl is true. Confirm we have a recovery context.
    const hash = window.location.hash;
    if (hash.includes("type=recovery") || hash.includes("access_token")) {
      setReady(true);
    } else {
      // Allow already-authed users to change their password too.
      supabase.auth.getSession().then(({ data }) => setReady(!!data.session));
    }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) return toast.error("Passwords don't match.");
    if (password.length < 8) return toast.error("Use at least 8 characters.");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) return toast.error(error.message);
    toast.success("Password updated.");
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      <SiteHeader />
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5">
          <div className="text-center">
            <h1 className="text-3xl font-bold tracking-tight">Set new password</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Choose a strong password — at least 8 characters.
            </p>
          </div>

          {!ready && (
            <p className="text-sm text-muted-foreground text-center">
              This page is only valid from a recovery email link or while signed in.
            </p>
          )}

          <div>
            <Label htmlFor="new-password">New password</Label>
            <Input id="new-password" type="password" required minLength={8}
                   value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          <div>
            <Label htmlFor="confirm-password">Confirm password</Label>
            <Input id="confirm-password" type="password" required minLength={8}
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} />
          </div>
          <Button type="submit" disabled={busy || !ready} className="w-full">
            {busy ? "Saving…" : "Update password"}
          </Button>
        </form>
      </main>
      <SiteFooter />
    </div>
  );
};

export default ResetPassword;
